import { useCallback } from 'react';
import { getProject, getProjectById, postProject } from '../../../api/projects';
import { createdProjectRequest } from '../../../utils/request-objects';
import { CreateProjectSchema } from '../../../validation/project';
import { ZodError } from 'zod';
import { handleOneLevelZodError } from '../../../validation/utils';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../../../hooks/redux';
import { Msg } from '../../../store/actions';
import { PageProjectWebDto } from '../../../api/api-types';
import { CreateProjectModalData } from '../../../components/Modal/CreateProjectModal/types';
import useInspectItem from '../../../hooks/useInspectItem';

interface UseProjectDataProps {
  currentPage: number;
  dispatch: ReturnType<typeof useAppDispatch>;
  selectedPMId?: string;
  selectedStatus: string | null;
  setHandleValues: React.Dispatch<Msg>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setPageCurrentData: (data: PageProjectWebDto) => void;
  sortParam: { key: string; sortOrder: string };
  toggleAddProject: boolean;
  isPresale: boolean | null;
}

type UseProjectDataReturn = {
  createProject: (projectState: CreateProjectModalData) => Promise<void>;
  onSelectProject: (e: { id: number | null; value: string }) => void;
  getAllProjects: () => void;
  updateProjectsList: () => void;
  clearSelectedProject: () => void;
};

export const useProjectData = ({
  currentPage,
  dispatch,
  selectedPMId,
  selectedStatus,
  setHandleValues,
  setIsLoading,
  setPageCurrentData,
  sortParam,
  toggleAddProject,
  isPresale,
}: UseProjectDataProps): UseProjectDataReturn => {
  const getAllProjects = useCallback(() => {
    if (isPresale === false) isPresale = null; // to display all projects and [PRESALES] on "PROJECTS" tab in search dropdown

    getProject({
      sortParam: 'name,asc',
      isPresale,
      unpaged: true,
    }).then((data) => {
      if (!data) return;

      dispatch({ type: 'GET_PROJECTS_ALL', payload: data.content ?? [] });
    });
  }, []);

  const updateProjectsList = useCallback(() => {
    setIsLoading(true);

    getProject({
      page: currentPage,
      size: 20,
      pmId: selectedPMId,
      status: selectedStatus === 'Active',
      isPresale: isPresale,
      sortParam: `${sortParam.key},${sortParam.sortOrder}`,
    }).then((data) => {
      if (!data) return;

      setPageCurrentData(data);

      dispatch({ type: 'GET_PROJECTS', payload: data.content ?? [] });
      getAllProjects();

      setIsLoading(false);
    });
  }, [selectedStatus, selectedPMId, currentPage, sortParam]);

  const createProject = async (projectState: CreateProjectModalData) => {
    try {
      const project = createdProjectRequest(projectState);
      const validatedProject = CreateProjectSchema.parse(project);

      await postProject({
        data: validatedProject,
      });
    } catch (err) {
      if (err instanceof ZodError) {
        handleOneLevelZodError(err).forEach((issue) => {
          toast.error(issue.message);
        });
      } else {
        toast.error('Oops! Something went wrong during project creation');
      }
      return;
    }

    try {
      updateProjectsList();
    } catch (err) {
      toast.error('Failed to update projects list');
    }

    setHandleValues({
      type: 'ON_TOGGLE_VIEW',
      payload: {
        toggleAddProject: !toggleAddProject,
      },
    });
  };

  const { goToItemDetails } = useInspectItem('presales', '/presales/');
  const onSelectProject = (e: { id: number | null; value: string }) => {
    setHandleValues({
      type: 'SET_SELECTABLES',
      payload: { projectId: e.id, projectName: e.value },
    });

    if (e.id !== null) {
      if (e.value.includes('[PRESALE]')) {
        // for redirecting to [presale] detailed page when [PRESALE] chosen in search dropdown on "PROJECTS" tab
        setIsLoading(true);

        getProjectById(e.id).then((data) => {
          if (!data) return;
          setPageCurrentData({ content: [data], totalElements: 1 });

          dispatch({ type: 'GET_PROJECTS', payload: [data] });

          setIsLoading(false);
        });

        goToItemDetails({
          original: { id: e.id },
        });
      } else {
        setIsLoading(true);

        getProjectById(e.id).then((data) => {
          if (!data) return;
          setPageCurrentData({ content: [data], totalElements: 1 });

          dispatch({ type: 'GET_PROJECTS', payload: [data] });

          setIsLoading(false);
        });
      }
    } else {
      updateProjectsList();
    }
  };

  const clearSelectedProject = () =>
    setHandleValues({
      type: 'SET_SELECTABLES',
      payload: { projectId: null, projectName: '' },
    });

  return {
    createProject,
    onSelectProject,
    getAllProjects,
    updateProjectsList,
    clearSelectedProject,
  };
};
